import { format, formatDistanceToNow } from "date-fns"
import { enGB, nb, sv } from "date-fns/locale"
import type { Locale } from "date-fns"
import {
  type FormattingLocale,
  getFormattingLocale,
} from "@/v2-common/utils/utils.formatting"

/**
 * Default date-fns tokens per app locale. All locales use the 24-hour clock;
 * only the date shape differs: Norwegian "11.06.2026", English "11/06/2026",
 * Swedish ISO "2026-06-11" (the official Swedish all-numeric format).
 * See umbrella issue #914.
 */
const DATE_FORMATS: Record<FormattingLocale, string> = {
  "nb-NO": "dd.MM.yyyy",
  "sv-SE": "yyyy-MM-dd",
  "en-GB": "dd/MM/yyyy",
}

const DATE_TIME_FORMATS: Record<FormattingLocale, string> = {
  "nb-NO": "HH:mm - dd.MM.yyyy",
  "sv-SE": "HH:mm - yyyy-MM-dd",
  "en-GB": "HH:mm - dd/MM/yyyy",
}

/**
 * The date-fns Locale for the active app locale. Use for textual tokens
 * ("PPP", "MMMM") so month/day names follow the user's language.
 */
export function getDateFnsLocale(): Locale {
  const locale = getFormattingLocale()
  if (locale === "en-GB") return enGB
  if (locale === "sv-SE") return sv
  return nb
}

function defaultDateFormat(
  locale: FormattingLocale = getFormattingLocale(),
): string {
  return DATE_FORMATS[locale]
}

function defaultDateTimeFormat(
  locale: FormattingLocale = getFormattingLocale(),
): string {
  return DATE_TIME_FORMATS[locale]
}

export function formatDateTime(date: Date, dateTimeFormat?: string) {
  return format(date, dateTimeFormat ?? defaultDateTimeFormat(), {
    locale: getDateFnsLocale(),
  })
}

export function formatDate(date: Date, dateFormat?: string) {
  return format(date, dateFormat ?? defaultDateFormat(), {
    locale: getDateFnsLocale(),
  })
}

/**
 * "09:15" — a duration as a clock face, or a time of day. Not `format()`:
 * date-fns needs a Date and would read it in the local time zone, while this
 * takes a bare number of seconds.
 */
export function secondsToHHmm(seconds: number) {
  const total = Math.round(seconds / 60)
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
}

/**
 * A time zone's offset from UTC in hours on a given date, summer time
 * included. Read at noon so a day with a clock change gets the offset most of
 * its daylight is in.
 */
export function getUtcOffsetHours(timeZone: string, date: Date) {
  const noonUtc = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12),
  )
  return offsetMinutesAt(timeZone, noonUtc) / 60
}

// A time zone's offset from UTC in minutes at one instant.
function offsetMinutesAt(timeZone: string, instant: Date): number {
  // "1/15/2026, 1:00:00 PM GMT+01:00", or a bare "GMT" at UTC.
  const label = instant.toLocaleString("en-US", {
    timeZone,
    timeZoneName: "longOffset",
  })
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(label)
  if (!match) return 0
  const [, sign, hours, minutes] = match
  return (sign === "-" ? -1 : 1) * (Number(hours) * 60 + Number(minutes))
}

/**
 * The calendar day (yyyy-mm-dd, for a native date input) an instant falls on
 * in a time zone.
 */
export function toZonedDateInput(instant: Date, timeZone: string): string {
  // en-CA formats as yyyy-mm-dd.
  return instant.toLocaleDateString("en-CA", { timeZone })
}

// The UTC instant a calendar day (yyyy-mm-dd) starts at in a time zone. The
// offset is read twice: once at UTC midnight as a guess, then at the guessed
// instant, which lands on the right side of a clock change.
function zonedDayStart(day: string, timeZone: string): Date {
  const [year, month, date] = day.split("-").map(Number)
  const wallMidnight = Date.UTC(year, month - 1, date)
  const guess =
    wallMidnight - offsetMinutesAt(timeZone, new Date(wallMidnight)) * 60_000
  return new Date(
    wallMidnight - offsetMinutesAt(timeZone, new Date(guess)) * 60_000,
  )
}

/**
 * Converts an inclusive day range (yyyy-mm-dd) picked in a time zone to the
 * inclusive UTC instants a query expects: `from` at the start of its day, `to`
 * at the last whole second of its day. Stored timestamps are UTC, so a day in
 * Norway starts at 22:00 or 23:00 UTC the evening before. The bound is a whole
 * second because the columns and parameters are DATETIME2(0): a `.999` bound
 * rounds up to the next midnight and would count that second in both ranges.
 */
export function zonedDayRangeToUtc(
  from: string,
  to: string,
  timeZone: string,
): { from: string; to: string } {
  const [year, month, date] = to.split("-").map(Number)
  const dayAfterTo = new Date(Date.UTC(year, month - 1, date + 1))
    .toISOString()
    .slice(0, 10)
  return {
    from: zonedDayStart(from, timeZone).toISOString(),
    to: new Date(
      zonedDayStart(dayAfterTo, timeZone).getTime() - 1000,
    ).toISOString(),
  }
}

export function getTimezoneOffset() {
  const date = new Date()
  return Math.abs(date.getTimezoneOffset() / 60)
}

/**
 * Validate and Format a date string to a formatted date string
 */
export function formatDateString(
  date: Date | string | null | undefined,
  fallback = "-",
): string {
  if (!date) return fallback
  if (typeof date === "string" && !Number.isNaN(Date.parse(date))) {
    return formatDate(new Date(date))
  }
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return formatDate(date)
  }
  return fallback
}

export function formatRelativeDateString(
  date: Date | string | null | undefined,
  fallback = "-",
): string {
  if (!date) return fallback
  const parsed = typeof date === "string" ? new Date(date) : date
  if (Number.isNaN(parsed.getTime())) return fallback
  const locale = getDateFnsLocale()
  return formatDistanceToNow(parsed, {
    addSuffix: true,
    locale: {
      ...locale,
      // Swap the approximation tokens for their exact counterparts so the
      // output drops the "about"/"omtrent"/"ungefär" qualifier in every
      // locale ("about 4 days ago" -> "4 days ago").
      formatDistance(token, count, options) {
        const exactToken = token
          .replace(/^about(X)/, "x")
          .replace(/^(almost|over)(X)/, "x") as typeof token
        return locale.formatDistance(exactToken, count, options)
      },
    },
  })
}
